import React, {useEffect, useReducer} from 'react';

import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {mediaTypeToComponent} from '../../util/mediaTypeToComponent';
import {TileMediaSpec, TileMediaTracking} from '../MediaCard';
import MessageMediaNotFound from '../MessageMediaNotFound';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {isComplete, useTileMedia} from './fetch';
import IN_MEMORY_CACHE from './fetch/inMemoryCache';
import {getKeyForTileMediaSpec} from './fetch/keys';

type TileMediaLoaderProps = {
  tileMediaSpec: TileMediaSpec;
  attemptNonexistentSteps: boolean;
  width: number;
  height: number;
  mediaWidth?: number;
  mediaHeight?: number;
  actualSize: boolean;
  controls: any;
  mediaPanelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  moleculeConfig?: any;
  disableRunLink?: boolean;
  maskOptions?: any;
  config: any;
  run: any;
  runSignature: any;
  runNotes?: string;
  customRunName?: string;
  labels: any[];
  globalStep: number;
  mediaKey: string;
  mediaIndex: number;
  omitRunLabel?: boolean;
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null;
  isWithinMediaBrowserModal?: boolean;
};

export const TileMediaLoader: React.FC<TileMediaLoaderProps> = React.memo(
  ({
    tileMediaSpec,
    attemptNonexistentSteps,
    width,
    height,
    omitRunLabel,
    ...props
  }) => {
    const {loading, tileMedia, status, getTileMediaTracking} = useTileMedia(
      tileMediaSpec,
      attemptNonexistentSteps
    );

    // TODO: this is an unfortunate hack to force a re-render once the tile media is loaded.
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);

    useEffect(() => {
      const checkStatus = () => {
        const status =
          IN_MEMORY_CACHE.TILE_MEDIA_STATUS_BY_KEY.get(tileMediaSpecKey);
        if (status && isComplete(status)) {
          forceUpdate();
        }
      };

      // Check immediately
      checkStatus();

      // Set up polling to check for status changes
      const interval = setInterval(checkStatus, 100);
      return () => clearInterval(interval);
    }, [tileMediaSpecKey]);

    const MediaComponent = mediaTypeToComponent(tileMediaSpec.mediaType);

    if (loading || !status) {
      return (
        <div style={{width, height}}>
          <Loader name="panel-content-loader" size="small" />
        </div>
      );
    }

    if (status?.hasError) {
      return (
        <MessageMediaNotFound
          stepIndex={tileMediaSpec.mediaStep}
          mediaKey={tileMediaSpec.mediaKey}
          mediaIndex={tileMediaSpec.mediaIndex}
        />
      );
    }

    return (
      <MediaComponent
        {...props}
        width={width}
        height={height}
        tileMedia={tileMedia}
        tileMediaSpec={tileMediaSpec}
        getMediaTracking={getTileMediaTracking}
        omitRunLabel={omitRunLabel}
      />
    );
  }
);
